/**
 * Browser Adapter Constants
 * 
 * Configuration constants for browser adapter service.
 * Following the apiClient pattern - all magic numbers extracted.
 */

import type { BrowserAdapterConfig } from './types';

// ============================================================================
// Adapter Configuration
// ============================================================================

/**
 * Default browser adapter configuration
 */
export const ADAPTER_CONFIG: Required<BrowserAdapterConfig> = {
  /**
   * Timeout for parent bridge postMessage requests
   * If parent doesn't respond within this time, fallback to next adapter
   */
  parentBridgeTimeout: 100, // 100ms

  /**
   * Enable debug logging
   * Automatically true in development
   */
  debug: import.meta.env.DEV,

  /**
   * Storage adapter priority (first available wins)
   * 1. native - localStorage/sessionStorage (best)
   * 2. parent - Parent window via postMessage (iframe fallback)
   * 3. indexedDB - IndexedDB (sometimes works in iframes)
   * 4. memory - In-memory fallback (session only)
   * 
   * NOTE: IndexedDB is currently mapped to memory adapter in browserAdapter.ts
   * This is a known limitation documented in README.md
   * IndexedDB support is planned for Phase 2
   */
  adapterPriority: ['native', 'parent', 'indexedDB', 'memory'],

  /**
   * Storage key prefix for namespacing
   * Prevents conflicts with other apps
   */
  keyPrefix: 'butter_',
} as const;

// ============================================================================
// Message Types
// ============================================================================

/**
 * postMessage types for parent bridge communication
 */
export const MESSAGE_TYPES = {
  STORAGE_GET: 'storage.get' as const,
  STORAGE_SET: 'storage.set' as const,
  STORAGE_REMOVE: 'storage.remove' as const,
  STORAGE_CLEAR: 'storage.clear' as const,
  STORAGE_KEYS: 'storage.keys' as const,
  STORAGE_RESPONSE: 'storage.response' as const,
  STORAGE_TEST: 'storage.test' as const, // Used by detector to test parent bridge availability
};

// ============================================================================
// Storage Keys
// ============================================================================

/**
 * Storage keys used by the application
 * Centralized to prevent typos and enable migration
 */
export const STORAGE_KEYS = {
  /**
   * Authentication tokens
   */
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  TOKEN_EXPIRY: 'token_expiry',

  /**
   * Tenant context
   */
  TENANT_CONTEXT: 'tenant_context',
  TENANT_HANDLE: 'tenant_handle',
  CLUB_ID: 'club_id',

  /**
   * Booking state
   */
  BOOKING_DRAFT: 'booking_draft',
  BOOKING_STEP: 'booking_step',

  /**
   * User preferences
   */
  THEME: 'theme',
  LANGUAGE: 'language',

  /**
   * Feature flags
   */
  FEATURE_FLAGS: 'feature_flags',

  /**
   * Test key for capability detection
   */
  TEST_KEY: '__test__',
} as const;

// ============================================================================
// Adapter Names
// ============================================================================

/**
 * Human-readable adapter names for logging
 */
export const ADAPTER_NAMES = {
  native: 'Native localStorage',
  session: 'Native sessionStorage',
  parent: 'Parent Bridge (postMessage)',
  indexedDB: 'IndexedDB',
  memory: 'In-Memory (session only)',
} as const;

// ============================================================================
// Error Messages
// ============================================================================

/**
 * User-friendly error messages
 */
export const ERROR_MESSAGES = {
  /**
   * Storage operation failed
   */
  STORAGE_FAILED: 'Failed to save data. Please try again.',

  /**
   * No persistent storage available
   */
  NO_PERSISTENCE: 'Data will be lost when you close this page. For better experience, enable cookies or use our standalone site.',

  /**
   * Parent bridge not available
   */
  NO_PARENT_BRIDGE: 'iframe storage not available. Add parent bridge script for better experience.',

  /**
   * All adapters failed
   */
  ALL_ADAPTERS_FAILED: 'Unable to save data. Please check your browser settings.',
} as const;

// ============================================================================
// Timeouts
// ============================================================================

/**
 * Timeout configuration for various operations
 * 
 * IMPORTANT: These timeouts are for CAPABILITY TESTING during initialization.
 * Actual storage operations use ADAPTER_CONFIG.parentBridgeTimeout (100ms).
 * 
 * Short timeouts allow quick fallback to next adapter if unavailable.
 * Actual operations have same timeout - this is acceptable because:
 * 1. postMessage is typically < 5ms on modern browsers
 * 2. 100ms is generous for cross-origin iframe communication
 * 3. Slow connections will fail fast and use memory adapter (with warning)
 */
export const TIMEOUTS = {
  /**
   * Parent bridge postMessage timeout (ms)
   * Used for both capability testing AND actual storage operations
   * 
   * @see ADAPTER_CONFIG.parentBridgeTimeout for actual operation timeout
   */
  PARENT_BRIDGE_MS: 100,

  /**
   * IndexedDB operation timeout (ms)
   * Used for capability testing only (IndexedDB adapter not yet implemented)
   */
  INDEXEDDB_MS: 200,

  /**
   * Storage test operation timeout (ms)
   * Used for quick localStorage/sessionStorage availability checks
   */
  STORAGE_TEST_MS: 50,
} as const;

// ============================================================================
// IndexedDB Configuration
// ============================================================================

/**
 * IndexedDB database configuration
 */
export const INDEXEDDB_CONFIG = {
  /**
   * Database name
   */
  DB_NAME: 'butter_storage',

  /**
   * Database version
   */
  DB_VERSION: 1,

  /**
   * Object store name
   */
  STORE_NAME: 'key_value',

  /**
   * Key path for object store
   */
  KEY_PATH: 'key',
} as const;
